/**
 * Per-insurer coverage pages — `/insurance/<slug>` (CH only, `page:insurance`
 * gate). One page per entry; the hub (`/insurance`) links to a page from that
 * insurer's example card.
 *
 * Structural data only — slug → CMS insurer id + brand name. **Packages are
 * not listed here**: they come from the CMS at render time (every package
 * except the `*-other` free-text option), so adding or renaming a package in
 * Payload never needs a code change and the page can't drift from the checker
 * sitting above it.
 *
 * Copy lives in `messages/*.json` under `insurancePages`: shared sentences
 * take `{insurer}`, and an optional per-package paragraph sits at
 * `insurers.<slug>.packages.<packageId>` (Helsana's are written; the rest are
 * for Nadjib to fill, and the card simply renders without the paragraph until
 * he does).
 *
 * `released` is the ship gate: a page stays out of the routes, the sitemap,
 * the markdown mirrors and every link until it's flipped. All fourteen went
 * OFF (Nadjib, INS-3, 2026-08-19) so the new checker page could ship while he
 * writes the per-insurer content — he'd rather have no page than a thin one
 * earning impressions. Helsana is back ON (Nadjib, 2026-08-21): its package
 * copy is the only set written, so it's the one page that isn't thin. The
 * other thirteen stay OFF until their copy lands. The reimbursement figures
 * come from the CMS and stay accurate meanwhile, so flipping one back to
 * `true` is the whole re-release: its route, sitemap entry, mirror, hub CTA
 * and "View all insurers" card all return with it.
 *
 * `next.config.ts` imports `LOCALIZED_SLUGS`, which derives the translated
 * `/versicherung/<slug>` and `/assurance/<slug>` URLs from this list, so keep
 * this module free of React and app imports.
 */
export const INSURER_PAGES = {
  assura: { insurerId: "assura", name: "Assura", released: true },
  atupri: { insurerId: "atupri", name: "Atupri", released: true },
  axa: { insurerId: "axa", name: "AXA", released: true },
  concordia: { insurerId: "concordia", name: "Concordia", released: true },
  css: { insurerId: "css", name: "CSS", released: true },
  egk: { insurerId: "egk", name: "EGK", released: true },
  "groupe-mutuel": {
    insurerId: "groupe-mutuel",
    name: "Groupe Mutuel",
    released: true,
  },
  helsana: { insurerId: "helsana", name: "Helsana", released: true },
  kpt: { insurerId: "kpt", name: "KPT", released: true },
  oekk: { insurerId: "oekk", name: "ÖKK", released: true },
  sanitas: { insurerId: "sanitas", name: "Sanitas", released: true },
  swica: { insurerId: "swica", name: "SWICA", released: true },
  sympany: { insurerId: "sympany", name: "Sympany", released: true },
  visana: { insurerId: "visana", name: "Visana", released: true },
} as const

export type InsurerPageSlug = keyof typeof INSURER_PAGES

/** Every insurer with a page written, released or not. */
export const ALL_INSURER_PAGE_SLUGS = Object.keys(
  INSURER_PAGES
) as readonly InsurerPageSlug[]

/** The pages that actually ship — routes, sitemap, hub links, mirrors. */
export const INSURER_PAGE_SLUGS = ALL_INSURER_PAGE_SLUGS.filter(
  (slug) => INSURER_PAGES[slug].released
)

export function isInsurerPageSlug(slug: string): slug is InsurerPageSlug {
  return (
    slug in INSURER_PAGES && INSURER_PAGES[slug as InsurerPageSlug].released
  )
}

/** The page slug for a CMS insurer id, if that insurer has a page. */
export function insurerPageSlugFor(
  insurerId: string
): InsurerPageSlug | undefined {
  return INSURER_PAGE_SLUGS.find(
    (slug) => INSURER_PAGES[slug].insurerId === insurerId
  )
}

/**
 * The `*-other` package is the checker's "my plan isn't listed" free-text
 * escape hatch, not a product anyone holds — never a card on the page.
 */
export function isRealPackageId(packageId: string): boolean {
  return !packageId.endsWith("-other")
}

/** The four check-ups a package's reimbursement is quoted for, in page order. */
export const PAGE_COVERAGE_SERVICE_IDS = [
  "core-mri",
  "advanced-mri",
  "pro-mri",
  "blood-test",
] as const
